import type { BuildingDefinition, BuildingType, Seat, UnitDefinition, UnitType } from "./game-types.js";

export const BUILDING_TYPES = [
  "gold_mine",
  "watchtower",
  "wolf_den",
  "wall",
  "sumpgap",
  "acorn_catapult",
  "mushroom",
  "bomb",
] as const satisfies readonly BuildingType[];

export const GAME_CONFIG = {
  lanes: 5,
  columnsPerSide: 7,
  worldWidth: 1200,
  leftLaneEndX: 48,
  rightLaneEndX: 1152,
  firstBuildingX: 120,
  buildingColumnGap: 70,
  startingGold: 425,
  simulationTickMs: 50,
  snapshotIntervalMs: 100,
  goldIntervalMs: 15_000,
  goldClaimValue: 25,
  maxPendingGold: 3,
  unitPurchaseCooldownMs: 6_000,
  maxDenQueue: 5,
  queueReleaseIntervalMs: 1_600,
  bombDamage: 450,
  towerRange: 300,
  towerDamage: 16,
  towerAttackIntervalMs: 2_000,
  towerImpactDelayMs: 430,
  catapultRange: 330,
  catapultDamage: 24,
  catapultSplashRadius: 48,
  catapultAttackIntervalMs: 5_200,
  catapultImpactDelayMs: 1_000,
  sumpgapChewCooldownMs: 12_000,
  sumpgapImpactDelayMs: 430,
  wolfAttackRange: 22,
  buildingCollisionRadius: 34,
  unitCollisionRadius: 18,
  unitFormationProximity: 56,
  eventLifetimeMs: 1_500,
  aiDecisionIntervalMs: 650,
} as const;

export const BUILDING_DEFINITIONS: Record<BuildingType, BuildingDefinition> = {
  gold_mine: {
    type: "gold_mine",
    name: "Guldgruva",
    description: "Bryter en klickbar guldkista var femtonde sekund. Rymmer tre kistor.",
    cost: 125,
    maxHealth: 240,
    placementCooldownMs: 8_000,
  },
  watchtower: {
    type: "watchtower",
    name: "Vakttorn",
    description: "Skjuter automatiskt på det närmaste fiendedjuret i samma lane.",
    cost: 250,
    maxHealth: 360,
    placementCooldownMs: 12_000,
  },
  wolf_den: {
    type: "wolf_den",
    name: "Varglya",
    description: "Köper fyra specialiserade djur direkt eller samlar upp till fem i en anfallskö.",
    cost: 200,
    maxHealth: 320,
    placementCooldownMs: 10_000,
  },
  wall: {
    type: "wall",
    name: "Stenmur",
    description: "En mycket tålig mur som hejdar fiender medan resten av skogen försvarar sig.",
    cost: 150,
    maxHealth: 1_100,
    placementCooldownMs: 6_000,
  },
  sumpgap: {
    type: "sumpgap",
    name: "Sumpgap",
    description: "Mossig sump-padda som slukar ett djur och når över en mur direkt framför sig.",
    cost: 225,
    maxHealth: 300,
    placementCooldownMs: 12_000,
  },
  acorn_catapult: {
    type: "acorn_catapult",
    name: "Ollonkatapult",
    description: "Skjuter långsamma ollon som ger måttlig skada mot täta grupper i samma lane.",
    cost: 275,
    maxHealth: 260,
    placementCooldownMs: 18_000,
  },
  mushroom: {
    type: "mushroom",
    name: "Flugsvamp",
    description: "Giftig engångsfälla som förgör sig själv och det första fiendedjuret som äter den.",
    cost: 90,
    maxHealth: 1,
    placementCooldownMs: 8_000,
  },
  bomb: {
    type: "bomb",
    name: "Krutbomb",
    description: "Spränger en valfri egen tile och fyra raka grannar utan att ersätta byggnaden där.",
    cost: 175,
    maxHealth: 1,
    placementCooldownMs: 20_000,
  },
};

export const UNIT_DEFINITIONS: Record<UnitType, UnitDefinition> = {
  wolf: {
    type: "wolf",
    name: "Skogsvarg",
    cost: 110,
    maxHealth: 170,
    speed: 20,
    damage: 18,
    attackIntervalMs: 1_500,
    buildingDamageMultiplier: 1,
    towerDamageMultiplier: 1,
  },
  rabbit: {
    type: "rabbit",
    name: "Stridshare",
    cost: 145,
    maxHealth: 140,
    speed: 26,
    damage: 14,
    attackIntervalMs: 1_500,
    buildingDamageMultiplier: 1,
    towerDamageMultiplier: 1,
  },
  beaver: {
    type: "beaver",
    name: "Stridsbäver",
    cost: 180,
    maxHealth: 200,
    speed: 16,
    damage: 15,
    attackIntervalMs: 1_500,
    buildingDamageMultiplier: 3,
    towerDamageMultiplier: 1,
  },
  hedgehog: {
    type: "hedgehog",
    name: "Pansarigelkott",
    cost: 150,
    maxHealth: 150,
    speed: 22,
    damage: 14,
    attackIntervalMs: 1_350,
    buildingDamageMultiplier: 1,
    towerDamageMultiplier: 0.4,
  },
};

export function oppositeSeat(seat: Seat): Seat {
  return seat === "p1" ? "p2" : "p1";
}

export function movementDirection(seat: Seat): 1 | -1 {
  return seat === "p1" ? 1 : -1;
}

export function laneEndXForSeat(seat: Seat): number {
  return seat === "p1" ? GAME_CONFIG.leftLaneEndX : GAME_CONFIG.rightLaneEndX;
}

export function buildingWorldX(seat: Seat, column: number): number {
  const distanceFromLeft = GAME_CONFIG.firstBuildingX + column * GAME_CONFIG.buildingColumnGap;
  return seat === "p1" ? distanceFromLeft : GAME_CONFIG.worldWidth - distanceFromLeft;
}

export function projectWorldXForViewer(worldX: number, viewer: Seat): number {
  return viewer === "p1" ? worldX : GAME_CONFIG.worldWidth - worldX;
}
